import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { doc, updateDoc, addDoc, collection, serverTimestamp, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useConvenios, useConvenioContracts, useAfianzadoras, useComisionConfig, useComisionLedger } from '../contexts/DataContext';
import { resolveParticipacion, getPrimaAmount, getFacturasVigentes, formatNumeroFianza } from '../utils/calculations';
import { useUser } from '../components/UserContext';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Button } from '../components/button';
import { Badge } from '../components/badge';
import { Avatar } from '../components/avatar';
import { Input } from '../components/input';
import { Textarea } from '../components/textarea';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../components/table';
import { Field, Label } from '../components/fieldset';
import { ComisionEditorDialog } from '../components/ComisionEditorDialog';
import BeneficiarioDialog from '../components/BeneficiarioDialog';
import ProyectoDialog from '../components/ProyectoDialog';
import ConvenioContractDialog from '../components/ConvenioContractDialog';
import { ContratoView } from '../components/ContratoView';
import SearchableListbox from '../components/SearchableListbox';
import { UploadFianza } from '../components/UploadFianza';
import { logBitacora } from '../utils/bitacora';
import { uploadConvenioContract } from '../api/convenioUpload';
import InviteConvenioUserDialog from '../components/InviteConvenioUserDialog';
import { BitacoraTimeline } from '../components/BitacoraTimeline';
import ConvenioHeader from '../components/convenio/ConvenioHeader';
import ConvenioResumen from '../components/convenio/ConvenioResumen';
import { ClientEstadisticas } from '../components/ClientEstadisticas';
import { ClientComisiones } from '../components/ClientComisiones';
import ProveedoresChaseList from '../components/convenio/ProveedoresChaseList';
import ContratoFianzasPanel from '../components/convenio/ContratoFianzasPanel';
import SectionCard from '../components/convenio/SectionCard';
import { useFrozenColumns, frozenCellClass, frozenHeadClass } from '../hooks/useFrozenColumns';
import {
  aggregateProveedores,
  computeEmittedFianzas,
  computePendingFianzas,
  dueLabel,
  getFianzaPorcentaje,
  getFianzaStatus,
} from '../utils/convenioCompliance';
import {
  ensureFacturas,
  getEndososAbiertos,
  clasificarEndosoPendiente,
  ESTADO_FIANZA,
} from '../utils/fianzaEventos';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilSquareIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  FolderIcon,
  BanknotesIcon,
  InformationCircleIcon,
  UserGroupIcon,
  ClockIcon,
  DocumentTextIcon,
  BuildingOffice2Icon,
  PresentationChartLineIcon,
} from '@heroicons/react/20/solid';

// Per-convenio participación defaults: how each afianzadora splits the comisión
// between the agente del convenio (beneficiario) and the agente del fiado for
// THIS convenio. Every afianzadora shows prefilled with its effective value
// (convenio → org → 70/30); only rows that differ from the org baseline are
// persisted on the convenio. Per-fianza deals live on the fianza itself
// (participacionOverride), not here. Owner-only money config.
const ParticipacionConvenioCard = ({ convenio, convenioId, contracts, updateConvenio, user, userData }) => {
  const { config } = useComisionConfig();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Only the afianzadoras this convenio actually touches: emisores seen on
  // its fianzas plus any override already saved here. The org catalog does
  // NOT belong in this list — it drowns the real rows in name variants.
  const emisores = useMemo(() => {
    const set = new Set();
    Object.keys(convenio?.participacion || {}).forEach((k) => set.add(k));
    (contracts || []).forEach((c) => (c.fianzas || []).forEach((f) => {
      const name = f.xmlFactura?.emisor?.nombre || f.xmlFianza?.emisor?.nombre || '';
      if (name) set.add(name);
    }));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [convenio?.participacion, contracts]);

  const baselineFor = (emisor) => resolveParticipacion(emisor, config, null);

  useEffect(() => {
    const next = {};
    emisores.forEach((emisor) => {
      const propio = convenio?.participacion?.[emisor];
      next[emisor] = propio && propio.beneficiario != null && propio.fiado != null
        ? { beneficiario: propio.beneficiario, fiado: propio.fiado }
        : { ...resolveParticipacion(emisor, config, null) };
    });
    setDraft(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emisores, convenio?.participacion, config]);

  const difiere = (emisor, entry) => {
    const base = baselineFor(emisor);
    return Number(entry?.beneficiario) !== base.beneficiario ||
           Number(entry?.fiado) !== base.fiado;
  };

  const setPct = (emisor, campo, raw) => {
    setDraft((prev) => ({ ...prev, [emisor]: { ...(prev[emisor] || {}), [campo]: raw } }));
    setError(null);
  };

  // On blur: clamp the edited side and complement the other one.
  const normalizeRow = (emisor, campo) => {
    setDraft((prev) => {
      const entry = prev[emisor] || {};
      const val = parseFloat(entry[campo]);
      if (Number.isNaN(val)) return prev;
      const clamped = Math.min(100, Math.max(0, val));
      const otro = Math.round((100 - clamped) * 100) / 100;
      const next = campo === 'beneficiario'
        ? { beneficiario: clamped, fiado: otro }
        : { fiado: clamped, beneficiario: otro };
      return { ...prev, [emisor]: next };
    });
  };

  const resetRow = (emisor) => {
    setDraft((prev) => ({ ...prev, [emisor]: { ...baselineFor(emisor) } }));
    setError(null);
  };

  const save = async () => {
    setError(null);
    const clean = {};
    for (const emisor of emisores) {
      const entry = draft[emisor] || {};
      const beneficiario = Number(entry.beneficiario);
      const fiado = Number(entry.fiado);
      if (
        Number.isNaN(beneficiario) || Number.isNaN(fiado) ||
        beneficiario < 0 || beneficiario > 100 || fiado < 0 || fiado > 100
      ) {
        setError(`Porcentajes inválidos para ${emisor}: deben estar entre 0 y 100.`);
        return;
      }
      if (beneficiario + fiado > 100) {
        setError(`La suma de porcentajes de ${emisor} no puede exceder 100.`);
        return;
      }
      if (difiere(emisor, { beneficiario, fiado })) {
        clean[emisor] = { beneficiario, fiado };
      }
    }
    setSaving(true);
    try {
      await updateConvenio(convenioId, { participacion: clean });
      await logBitacora({
        action: 'UPDATE',
        collection: 'convenios',
        documentId: convenioId,
        metadata: {
          convenioId,
          convenioName: convenio?.nombre || null,
          description: 'Actualizó la participación de comisiones por afianzadora',
        },
        user,
        userData,
      });
    } catch (e) {
      console.error('Error guardando participación del convenio:', e);
      setError('No se pudo guardar la participación.');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-20 rounded-lg border border-azul-surelia/30 px-2 py-1.5 text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-azul-surelia/40 focus:border-azul-surelia';

  return (
    <SectionCard
          tone="gradient"
      icon={BanknotesIcon}
      title="Participación por afianzadora"
      action={
        <Button color="white" onClick={save} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      }
      bodyClassName="p-0"
    >
      {error && (
        <div className="mx-5 mt-4 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
      )}
      {emisores.length === 0 && (
        <div className="px-5 py-6 text-sm text-zinc-400">
          Las afianzadoras aparecerán aquí al registrar fianzas en el convenio.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-azul-surelia/15">
              <th className="py-2.5 px-5 font-medium">Afianzadora</th>
              <th className="py-2.5 px-3 font-medium">Convenio (beneficiario) %</th>
              <th className="py-2.5 px-3 font-medium">Agente del fiado %</th>
              <th className="py-2.5 px-3 font-medium">Origen</th>
              <th className="py-2.5 px-5 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {emisores.map((emisor, i) => {
              const entry = draft[emisor] || {};
              const cambiado = difiere(emisor, entry);
              const enOrg = Boolean(config?.participacionConvenio?.[emisor]);
              return (
                <tr key={emisor} className={`border-b border-gray-100 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}>
                  <td className="py-2 px-5 font-medium text-gray-800">{emisor}</td>
                  <td className="py-2 px-3">
                    <div className="relative inline-block">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={entry.beneficiario ?? ''}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => setPct(emisor, 'beneficiario', e.target.value)}
                        onBlur={() => normalizeRow(emisor, 'beneficiario')}
                        className={inputCls}
                      />
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={entry.fiado ?? ''}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => setPct(emisor, 'fiado', e.target.value)}
                      onBlur={() => normalizeRow(emisor, 'fiado')}
                      className={inputCls}
                    />
                  </td>
                  <td className="py-2 px-3">
                    {cambiado ? (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-azul-surelia/10 text-azul-surelia border border-azul-surelia/20">
                        Convenio
                      </span>
                    ) : enOrg ? (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 border border-zinc-200">
                        Organización
                      </span>
                    ) : (
                      <span className="text-[10px] px-1.5 py-0.5 rounded border border-dashed border-zinc-300 text-zinc-400">
                        Por defecto
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-5 text-right">
                    {cambiado && (
                      <button
                        type="button"
                        onClick={() => resetRow(emisor)}
                        className="text-xs text-azul-surelia hover:underline"
                        title="Volver al valor de la organización"
                      >
                        Restablecer
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
};

const TABS = [
  { key: 'resumen', label: 'Resumen' },
  { key: 'contratos', label: 'Contratos' },
  { key: 'fianzas', label: 'Fianzas' },
  { key: 'proyectos', label: 'Proyectos' },
  { key: 'proveedores', label: 'Proveedores' },
  { key: 'comisiones', label: 'Comisiones' },
  { key: 'configuracion', label: 'Configuración' },
];
const TAB_KEYS = new Set(TABS.map((t) => t.key));

const NOTA_CATEGORIAS = [
  { value: 'general', label: 'General', color: 'bg-gray-100 text-gray-700' },
  { value: 'urgente', label: 'Urgente', color: 'bg-red-100 text-red-700' },
  { value: 'seguimiento', label: 'Seguimiento', color: 'bg-amber-100 text-amber-700' },
  { value: 'documentacion', label: 'Documentación', color: 'bg-blue-100 text-blue-700' },
  { value: 'pago', label: 'Pago', color: 'bg-green-100 text-green-700' },
  { value: 'legal', label: 'Legal', color: 'bg-purple-100 text-purple-700' },
];

const AVATAR_COLORS = [
  'bg-azul-surelia/20 text-azul-surelia',
  'bg-verde-lima/30 text-azul-surelia',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-teal-100 text-teal-700',
];

const formatDate = (ts) => {
  if (!ts) return '-';
  const ms = ts?.toMillis?.() || ts;
  return new Date(ms).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatMonto = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

// Case- and accent-insensitive matching for the tab search boxes.
const normalizar = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const FIANZA_ESTATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estatus' },
  { value: 'vigente', label: 'Vigente' },
  { value: 'vencida', label: 'Vencida' },
  { value: 'pendiente', label: 'Por iniciar' },
  { value: 'cancelada', label: 'Cancelada' },
  { value: 'anulada', label: 'Anulada' },
  { value: 'sustituida', label: 'Sustituida' },
];

const FIANZA_VIGENCIA_OPTIONS = [
  { value: 'all', label: 'Cualquier vigencia' },
  { value: 'v30', label: 'Vence en ≤ 30 días' },
  { value: 'v90', label: 'Vence en ≤ 90 días' },
  { value: 'vencida', label: 'Vigencia terminada' },
];

// Same rollup vocabulary as the general /app/contratos page.
const URGENCIA_ORDER = { vencido: 0, urgente: 1, proximo: 2, normal: 3, sinFecha: 4 };
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const PAGO_BADGE = {
  pagada: 'bg-green-100 text-green-800',
  parcial: 'bg-amber-100 text-amber-800',
  pendiente: 'bg-orange-100 text-orange-800',
};

// Cobro states with nothing left to collect. 'adeudo' is NOT here: it is money
// owed back to the afianzadora until it is liquidado.
const COBRO_SETTLED = ['cobrada', 'neutralizada', 'adeudo_liquidado'];

const COBRO_CHIP = {
  cobrada: { cls: 'bg-emerald-100 text-emerald-700', label: 'Cobrada' },
  parcial: { cls: 'bg-amber-100 text-amber-700', label: 'Cobro parcial' },
  pendiente: { cls: 'bg-orange-100 text-orange-800', label: 'Por cobrar' },
  sin_saldo: { cls: 'bg-zinc-100 text-zinc-500', label: 'Sin saldo' },
};

const ConvenioDetails = () => {
  const { convenioId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { conveniosById, conveniosLoading, updateConvenio } = useConvenios();
  const contracts = useConvenioContracts(convenioId);
  const { afianzadorasById } = useAfianzadoras();
  const { user, userData } = useUser();
  const { config: comisionConfig } = useComisionConfig();

  const convenio = conveniosById[convenioId];
  const beneficiarios = useMemo(() => convenio?.beneficiarios || [], [convenio?.beneficiarios]);
  const proyectos = useMemo(() => convenio?.proyectos || [], [convenio?.proyectos]);

  // Tab state — with backward compat: unknown / dropped tabs fall back to proveedores
  const rawTab = searchParams.get('tab');
  const activeTab = TAB_KEYS.has(rawTab) ? rawTab : 'resumen';
  const setActiveTab = (tab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };
  // ?proveedor= viene de los deep links de reportes: preselecciona esa tarjeta
  // ya en el primer render, que es lo que lee ProveedorCard para abrirse.
  const proveedorParam = searchParams.get('proveedor');
  const [expandKey, setExpandKey] = useState(proveedorParam);
  useEffect(() => {
    if (proveedorParam) setExpandKey(proveedorParam);
  }, [proveedorParam]);

  // Dialog states
  const [isBeneficiarioOpen, setIsBeneficiarioOpen] = useState(false);
  const [editBeneficiario, setEditBeneficiario] = useState(null);
  const [isProyectoOpen, setIsProyectoOpen] = useState(false);
  const [editProyecto, setEditProyecto] = useState(null);
  const [isContractOpen, setIsContractOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  // ContratoView
  const [selectedContrato, setSelectedContrato] = useState(null);
  const [isContratoViewOpen, setIsContratoViewOpen] = useState(false);
  // Row to scroll back to and flash after the ContratoView dialog closes.
  const [highlightContratoId, setHighlightContratoId] = useState(null);
  // Freeze panes: Contratos pins the chevron + No. de Contrato, Fianzas pins
  // Tipo + No. Fianza, so the identifying columns survive horizontal scroll.
  const contratosFrozen = useFrozenColumns(2, activeTab);
  const fianzasFrozen = useFrozenColumns(2, activeTab);

  // Contratos tab filters
  const [contratosSearch, setContratosSearch] = useState('');
  const [proveedorFilter, setProveedorFilter] = useState('all');
  const [beneficiarioFilter, setBeneficiarioFilter] = useState('all');
  // Fianzas tab filters
  const [fianzasSearch, setFianzasSearch] = useState('');
  const [fianzasProveedor, setFianzasProveedor] = useState('all');
  const [fianzasEstatus, setFianzasEstatus] = useState('all');
  const [fianzasVigencia, setFianzasVigencia] = useState('all');
  const [expandedContratoIds, setExpandedContratoIds] = useState(() => new Set());
  // Shared comisión editor (% + participación) opened from the Fianzas tab:
  // { fianza, contratoId, participacion } | null
  const [comisionEdit, setComisionEdit] = useState(null);
  // Ledger movements are already live in DataContext (empty for non-owners);
  // the editor needs the fianza's emisión movement to correct its %.
  const { movements: comisionMovements } = useComisionLedger();
  const comisionMovFor = (fianzaId) =>
    comisionMovements.find((m) => m.fianzaId === fianzaId && m.tipo === 'emision') || null;

  // What the comisión of a fianza adds up to across ALL its movements (emisión,
  // adicionales, sustituciones, reversas) and how much of it is already settled.
  // Reversal end-states carry nothing left to collect, so they count as settled.
  // Returns null for client users — the ledger listener is owner/operator only.
  const comisionResumenFor = (fianzaId) => {
    const movs = comisionMovements.filter((m) => m.fianzaId === fianzaId);
    if (movs.length === 0) return null;
    let total = 0;
    let cobrado = 0;
    let pendiente = 0;
    movs.forEach((m) => {
      const monto = Number(m.comisionMonto) || 0;
      total += monto;
      if (monto === 0) return;
      if (COBRO_SETTLED.includes(m.estadoCobro)) cobrado += monto;
      else pendiente += monto;
    });
    const estado =
      cobrado === 0 && pendiente === 0 ? 'sin_saldo'
      : pendiente === 0 ? 'cobrada'
      : cobrado === 0 ? 'pendiente'
      : 'parcial';
    return { total, cobrado, pendiente, estado };
  };

  // UploadFianza
  const [uploadFianzaOpen, setUploadFianzaOpen] = useState(false);
  const [uploadFianzaPayload, setUploadFianzaPayload] = useState(null);

  // Invite dialog
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  // Config edit
  const [isEditingConfig, setIsEditingConfig] = useState(false);
  const [editNombre, setEditNombre] = useState('');
  const [editDescripcion, setEditDescripcion] = useState('');

  // Users with access
  const [usersWithAccess, setUsersWithAccess] = useState([]);

  // Notas
  const [notas, setNotas] = useState([]);
  const [notasLoading, setNotasLoading] = useState(true);
  const [newNota, setNewNota] = useState('');
  const [newNotaCategoria, setNewNotaCategoria] = useState('general');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [savingNota, setSavingNota] = useState(false);

  useEffect(() => {
    if (convenio) {
      setEditNombre(convenio.nombre || '');
      setEditDescripcion(convenio.descripcion || '');
    }
  }, [convenio]);

  // Real-time listener for notas subcollection
  useEffect(() => {
    if (!convenioId) return;
    const notasQuery = query(
      collection(db, 'convenios', convenioId, 'notas'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      notasQuery,
      (snapshot) => {
        const data = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setNotas(data);
        setNotasLoading(false);
      },
      (err) => {
        console.error('Error fetching notas:', err);
        setNotasLoading(false);
      }
    );
    return () => unsubscribe();
  }, [convenioId]);

  // Fetch client users who have access to this convenio
  useEffect(() => {
    if (!convenioId || !userData?.org || userData?.rol === 'client') return;
    const fetchUsersWithAccess = async () => {
      try {
        const { getDocs: gd, query: q, where: w, collection: col } = await import('firebase/firestore');
        const usersQuery = q(
          col(db, 'usuarios'),
          w('org', '==', userData.org),
          w('rol', '==', 'client')
        );
        const snap = await gd(usersQuery);
        const users = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((u) => u.convenioAccess && convenioId in u.convenioAccess);
        setUsersWithAccess(users);
      } catch (err) {
        console.error('Error fetching users with access:', err);
      }
    };
    fetchUsersWithAccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [convenioId, userData?.org, isInviteOpen]);

  // ============ BENEFICIARIO CRUD ============

  const handleAddBeneficiario = useCallback(async (data) => {
    setDialogLoading(true);
    try {
      const newBeneficiario = {
        id: `ben_${Date.now()}`,
        nombre: data.nombre,
        rfc: data.rfc,
        createdAt: Date.now(),
      };
      await updateConvenio(convenioId, {
        beneficiarios: [...beneficiarios, newBeneficiario],
      });
      await logBitacora({
        action: 'UPDATE',
        collection: 'convenios',
        documentId: convenioId,
        metadata: {
          convenioId,
          convenioName: convenio?.nombre || null,
          fieldName: 'beneficiarios',
          itemName: data.nombre,
          description: `Agregó beneficiario '${data.nombre}' al convenio`,
        },
        user,
        userData,
      });
      setIsBeneficiarioOpen(false);
    } catch (error) {
      console.error('Error adding beneficiario:', error);
    } finally {
      setDialogLoading(false);
    }
  }, [convenioId, convenio?.nombre, beneficiarios, updateConvenio, user, userData]);

  const handleEditBeneficiario = useCallback(async (data) => {
    setDialogLoading(true);
    try {
      const updated = beneficiarios.map((b) =>
        b.id === data.id ? { ...b, nombre: data.nombre, rfc: data.rfc } : b
      );
      await updateConvenio(convenioId, { beneficiarios: updated });
      await logBitacora({
        action: 'UPDATE',
        collection: 'convenios',
        documentId: convenioId,
        metadata: {
          convenioId,
          convenioName: convenio?.nombre || null,
          fieldName: 'beneficiarios',
          itemName: data.nombre,
          description: `Editó beneficiario '${data.nombre}'`,
        },
        user,
        userData,
      });
      setIsBeneficiarioOpen(false);
      setEditBeneficiario(null);
    } catch (error) {
      console.error('Error editing beneficiario:', error);
    } finally {
      setDialogLoading(false);
    }
  }, [convenioId, convenio?.nombre, beneficiarios, updateConvenio, user, userData]);

  // ============ PROYECTO CRUD ============

  const handleAddProyecto = useCallback(async (data) => {
    setDialogLoading(true);
    try {
      const newProyecto = { id: `proy_${Date.now()}`, nombre: data.nombre };
      await updateConvenio(convenioId, {
        proyectos: [...proyectos, newProyecto],
      });
      await logBitacora({
        action: 'UPDATE',
        collection: 'convenios',
        documentId: convenioId,
        metadata: {
          convenioId,
          convenioName: convenio?.nombre || null,
          fieldName: 'proyectos',
          itemName: data.nombre,
          description: `Agregó proyecto '${data.nombre}' al convenio`,
        },
        user,
        userData,
      });
      setIsProyectoOpen(false);
    } catch (error) {
      console.error('Error adding proyecto:', error);
    } finally {
      setDialogLoading(false);
    }
  }, [convenioId, convenio?.nombre, proyectos, updateConvenio, user, userData]);

  const handleEditProyecto = useCallback(async (data) => {
    setDialogLoading(true);
    try {
      const updated = proyectos.map((p) =>
        p.id === data.id ? { ...p, nombre: data.nombre } : p
      );
      await updateConvenio(convenioId, { proyectos: updated });
      await logBitacora({
        action: 'UPDATE',
        collection: 'convenios',
        documentId: convenioId,
        metadata: {
          convenioId,
          convenioName: convenio?.nombre || null,
          fieldName: 'proyectos',
          itemName: data.nombre,
          description: `Editó proyecto '${data.nombre}'`,
        },
        user,
        userData,
      });
      setIsProyectoOpen(false);
      setEditProyecto(null);
    } catch (error) {
      console.error('Error editing proyecto:', error);
    } finally {
      setDialogLoading(false);
    }
  }, [convenioId, convenio?.nombre, proyectos, updateConvenio, user, userData]);

  // ============ CONTRACT CREATION ============

  const handleAddContract = useCallback(async (data) => {
    setDialogLoading(true);
    try {
      await uploadConvenioContract(data.contractFile, {
        convenioId,
        beneficiarioId: data.beneficiarioId,
        proyectoId: data.proyectoId,
      });

      const beneficiario = beneficiarios.find((b) => b.id === data.beneficiarioId);
      const proyecto = proyectos.find((p) => p.id === data.proyectoId);
      const parts = [`Subió contrato al convenio`];
      if (beneficiario) parts.push(`beneficiario '${beneficiario.nombre}'`);
      if (proyecto) parts.push(`proyecto '${proyecto.nombre}'`);
      parts.push('(procesando archivo)');

      await logBitacora({
        action: 'CREATE',
        collection: 'contracts',
        documentId: convenioId,
        metadata: { convenioId, convenioName: convenio?.nombre || null, description: parts.join(', ') },
        user,
        userData,
      });
      setIsContractOpen(false);
    } catch (error) {
      console.error('Error uploading contract:', error);
    } finally {
      setDialogLoading(false);
    }
  }, [convenioId, convenio?.nombre, user, userData, beneficiarios, proyectos]);

  // ============ CONFIG SAVE ============

  const handleSaveConfig = useCallback(async () => {
    setDialogLoading(true);
    try {
      await updateConvenio(convenioId, {
        nombre: editNombre.trim(),
        descripcion: editDescripcion.trim(),
      });
      await logBitacora({
        action: 'UPDATE',
        collection: 'convenios',
        documentId: convenioId,
        metadata: { convenioId, convenioName: convenio?.nombre || null, description: `Actualizó información del convenio` },
        user,
        userData,
      });
      setIsEditingConfig(false);
    } catch (error) {
      console.error('Error saving config:', error);
    } finally {
      setDialogLoading(false);
    }
  }, [convenioId, convenio?.nombre, editNombre, editDescripcion, updateConvenio, user, userData]);

  const handleAddNota = useCallback(async () => {
    if (!newNota.trim()) return;
    setSavingNota(true);
    try {
      await addDoc(collection(db, 'convenios', convenioId, 'notas'), {
        texto: newNota.trim(),
        categoria: newNotaCategoria,
        completed: false,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        userName: userData?.nombre || '',
      });
      setNewNota('');
    } catch (error) {
      console.error('Error adding nota:', error);
    } finally {
      setSavingNota(false);
    }
  }, [convenioId, newNota, newNotaCategoria, user, userData]);

  const handleToggleNota = useCallback(async (notaId, currentCompleted) => {
    try {
      await updateDoc(doc(db, 'convenios', convenioId, 'notas', notaId), {
        completed: !currentCompleted,
      });
    } catch (error) {
      console.error('Error toggling nota:', error);
    }
  }, [convenioId]);

  const handleToggleStatus = useCallback(async () => {
    const newStatus = convenio?.estatus === 'activo' ? 'inactivo' : 'activo';
    await updateConvenio(convenioId, { estatus: newStatus });
    await logBitacora({
      action: 'UPDATE',
      collection: 'convenios',
      documentId: convenioId,
      metadata: {
        convenioId,
        convenioName: convenio?.nombre || null,
        fieldName: 'estatus',
        newValue: newStatus,
        description: `Cambió estatus del convenio a '${newStatus}'`,
      },
      user,
      userData,
    });
  }, [convenioId, convenio?.estatus, convenio?.nombre, updateConvenio, user, userData]);

  // ============ COMPUTED DATA ============

  const beneficiariosById = useMemo(() => {
    const map = {};
    beneficiarios.forEach((b) => { map[b.id] = b; });
    return map;
  }, [beneficiarios]);

  const proyectosById = useMemo(() => {
    const map = {};
    proyectos.forEach((p) => { map[p.id] = p; });
    return map;
  }, [proyectos]);

  const proveedoresAgg = useMemo(
    () => aggregateProveedores(contracts, beneficiariosById, proyectosById),
    [contracts, beneficiariosById, proyectosById]
  );
  const allFianzas = useMemo(
    () => computeEmittedFianzas(contracts, beneficiariosById, proyectosById),
    [contracts, beneficiariosById, proyectosById]
  );

  // Notas helpers
  const getCategoriaStyle = (cat) =>
    NOTA_CATEGORIAS.find((c) => c.value === cat) || NOTA_CATEGORIAS[0];
  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].substring(0, 2).toUpperCase();
  };
  const getAvatarColor = (uid) => {
    if (!uid) return AVATAR_COLORS[0];
    const hash = uid.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return AVATAR_COLORS[hash % AVATAR_COLORS.length];
  };
  const filteredNotas = useMemo(() => {
    if (filterCategoria === 'all') return notas;
    return notas.filter((n) => n.categoria === filterCategoria);
  }, [notas, filterCategoria]);

  // Coming back from the ContratoView dialog, scroll the row you opened back
  // into view and flash it — in a long Contratos/Proyectos list the overlay
  // otherwise drops you with no sense of where you were.
  useEffect(() => {
    if (!highlightContratoId) return undefined;
    // Wait out the dialog's leave transition so the scroll lock is released.
    const scrollT = setTimeout(() => {
      const el = document.querySelector(`[data-contrato-row="${highlightContratoId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
    const clearT = setTimeout(() => setHighlightContratoId(null), 3000);
    return () => { clearTimeout(scrollT); clearTimeout(clearT); };
  }, [highlightContratoId]);

  if (conveniosLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  if (!convenio) {
    return (
      <div className="text-center py-12">
        <Heading>Convenio no encontrado</Heading>
        <Button className="mt-4" onClick={() => navigate('/app/convenios')}>
          Volver a Convenios
        </Button>
      </div>
    );
  }

  const isReadOnly = userData?.rol === 'client';
  // Comisiones are owner business, both read and write: the ledger endpoints
  // (/override-pct, /override-split) are owner-only, and every other comisión
  // surface (ComisionLedgerPanel, /app/comisiones, the Comisiones tab, the
  // comisión reportes) is already hidden from operators. The columns below
  // follow the same line — an operator sees no % and no money here.
  const canVerComision = userData?.rol === 'owner';

  const openContrato = (contractId) => {
    const contract = contracts.find((c) => c.id === contractId);
    if (contract) {
      setSelectedContrato(contract);
      setIsContratoViewOpen(true);
    }
  };

  const handleProveedorAction = ({ type, contratoId, frKey, tipo, monto }) => {
    if (type !== 'subir_fianza') return;
    const contract = contracts.find((c) => c.id === contratoId);
    if (!contract) return;
    const fianzasReq = contract.fianzasRequeridas?.value || [];
    const idxStr = frKey.split('_req_')[1];
    const idx = parseInt(idxStr, 10);
    const matching = (!Number.isNaN(idx) && fianzasReq[idx]) ? [{ ...fianzasReq[idx], _index: idx }] : [];
    setUploadFianzaPayload({
      contratoId,
      contratoProveedor: contract.proveedor?.value || '',
      pendingFianzas: matching.length > 0 ? matching : fianzasReq.filter((fr) => !fr.fianzaId).map((fr, i) => ({ ...fr, _index: i })),
    });
    setUploadFianzaOpen(true);
  };

  const handleProveedorClickFromResumen = (key) => {
    setExpandKey(key);
    setActiveTab('proveedores');
  };

  // ============ TAB RENDERERS ============

  // Same lifecycle rollup the general /app/contratos page shows: terminal
  // fianzas count as "terminadas" and stay out of the monto afianzado. Shared
  // by the Contratos tab (row chips + expandable fianzas panel) and the
  // Proyectos tab (per-proyecto aggregates).
  const getContratoFianzaStats = (contrato) => {
    const now = Date.now();
    const fianzas = contrato.fianzas || [];
    const r = {
      emitidas: fianzas.length,
      vigentes: 0,
      porVencer: 0,
      vencidas: 0,
      porIniciar: 0,
      terminadas: 0,
      montoAfianzado: 0,
      primaVigente: 0,
      endososPendientes: 0,
      endososUrgencia: null,
      // The requirements themselves, enriched with dueDate/isOverdue/isFutura —
      // the expandable panel lists them, so a count alone isn't enough.
      pendientes: computePendingFianzas([contrato]),
      rows: [],
    };
    r.reqPendientes = r.pendientes.length;
    fianzas.forEach((fianza) => {
      const status = getFianzaStatus(fianza, now);
      const fin = fianza.finVigenciaFianza?.toMillis?.() || fianza.finVigenciaFianza || 0;
      const prima = getPrimaAmount(fianza) || 0;
      const facturasVigentes = getFacturasVigentes(ensureFacturas(fianza));
      const pagadas = facturasVigentes.filter((fc) => fc.pagada).length;
      const pagoEstado =
        facturasVigentes.length > 0 && pagadas === facturasVigentes.length
          ? 'pagada'
          : pagadas > 0
          ? 'parcial'
          : 'pendiente';
      const abiertos = status.estado === ESTADO_FIANZA.ACTIVA ? getEndososAbiertos(fianza) : [];
      abiertos.forEach((p) => {
        const u = clasificarEndosoPendiente(p, now).code;
        if (!r.endososUrgencia || URGENCIA_ORDER[u] < URGENCIA_ORDER[r.endososUrgencia]) {
          r.endososUrgencia = u;
        }
      });
      r.endososPendientes += abiertos.length;
      if (status.estado === ESTADO_FIANZA.ACTIVA) {
        r.montoAfianzado += Number(fianza.montoAfianzado) || 0;
        if (status.code === 'vigente') {
          r.vigentes += 1;
          r.primaVigente += prima;
          if (fin > 0 && fin - now <= THIRTY_DAYS) r.porVencer += 1;
        } else if (status.code === 'vencida') {
          r.vencidas += 1;
        } else {
          r.porIniciar += 1;
        }
      } else {
        r.terminadas += 1;
      }
      r.rows.push({
        fianza: { ...fianza, porcentajeInfo: getFianzaPorcentaje(fianza, contrato) },
        status,
        prima,
        pagoEstado,
        pagadas,
        facturasTotal: facturasVigentes.length,
        endososPendientes: abiertos.length,
      });
    });
    return r;
  };

  const toggleContratoExpanded = (contratoId) => {
    setExpandedContratoIds((prev) => {
      const next = new Set(prev);
      if (next.has(contratoId)) next.delete(contratoId);
      else next.add(contratoId);
      return next;
    });
  };

  const renderContratos = () => {

    const query = contratosSearch.trim().toLowerCase();
    const filteredContracts = contracts.filter((c) => {
      if (query && !(c.numeroContrato?.value || '').toString().toLowerCase().includes(query)) {
        return false;
      }
      if (proveedorFilter !== 'all' && (c.proveedor?.value || '') !== proveedorFilter) {
        return false;
      }
      if (beneficiarioFilter !== 'all' && (c.contratante?.value || '') !== beneficiarioFilter) {
        return false;
      }
      return true;
    });

    const uniqueSorted = (getter) => {
      const set = new Set();
      contracts.forEach((c) => {
        const v = getter(c);
        if (v) set.add(v);
      });
      return Array.from(set).sort((a, b) => a.localeCompare(b));
    };
    const proveedorOptions = [
      { value: 'all', label: 'Todos los proveedores' },
      ...uniqueSorted((c) => c.proveedor?.value).map((v) => ({ value: v, label: v })),
    ];
    const beneficiarioOptions = [
      { value: 'all', label: 'Todos los beneficiarios' },
      ...uniqueSorted((c) => c.contratante?.value).map((v) => ({ value: v, label: v })),
    ];

    return (
      <div className="space-y-4">
        {contracts.length === 0 ? (
          <div className="text-center py-8">
            <Text>No hay contratos en este convenio.</Text>
          </div>
        ) : (
          <>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
            <input
              type="text"
              value={contratosSearch}
              onChange={(e) => setContratosSearch(e.target.value)}
              placeholder="Buscar por No. de Contrato..."
              className="w-full sm:max-w-xs px-3 py-2 border border-azul-surelia/30 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-azul-surelia/40 focus:border-azul-surelia"
            />
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Proveedor:</label>
              <SearchableListbox
                value={proveedorFilter}
                onChange={setProveedorFilter}
                options={proveedorOptions}
                className="min-w-[220px] max-w-[260px]"
                placeholder="Selecciona proveedor"
                searchPlaceholder="Buscar proveedor..."
                popupClassName="w-[260px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Beneficiario:</label>
              <SearchableListbox
                value={beneficiarioFilter}
                onChange={setBeneficiarioFilter}
                options={beneficiarioOptions}
                className="min-w-[220px] max-w-[260px]"
                placeholder="Selecciona beneficiario"
                searchPlaceholder="Buscar beneficiario..."
                popupClassName="w-[260px]"
              />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
            <div className="relative">
              <Table onScroll={contratosFrozen.onScroll}>
                <TableHead className="bg-azul-surelia">
                  <TableRow>
                    <TableHeader
                      ref={contratosFrozen.setRef(0)}
                      style={{ left: contratosFrozen.offsets[0] }}
                      className={`text-white font-semibold py-3 border-b-0 w-8 ${frozenHeadClass}`}
                    ></TableHeader>
                    <TableHeader
                      ref={contratosFrozen.setRef(1)}
                      style={{ left: contratosFrozen.offsets[1] }}
                      className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                    >No. de Contrato</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Fianzas</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Proveedor</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Beneficiario</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Proyecto</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Monto Contrato</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Afianzado</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Estatus</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContracts.map((contrato, index) => {
                    const fianzaStats = getContratoFianzaStats(contrato);
                    const expanded = expandedContratoIds.has(contrato.id);
                    return (
                      <React.Fragment key={contrato.id}>
                      <TableRow
                        data-contrato-row={contrato.id}
                        className={`group cursor-pointer transition-colors duration-700 hover:bg-azul-surelia/5 ${
                          highlightContratoId === contrato.id
                            ? 'bg-cian-digital/25'
                            : index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                        }`}
                        onClick={() => openContrato(contrato.id)}
                      >
                        <TableCell
                          style={{ left: contratosFrozen.offsets[0] }}
                          className={`py-3 pr-0 ${frozenCellClass(index, highlightContratoId === contrato.id)}`}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleContratoExpanded(contrato.id);
                            }}
                            className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                            title={expanded ? 'Ocultar fianzas' : 'Ver fianzas'}
                          >
                            {expanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell
                          style={{ left: contratosFrozen.offsets[1] }}
                          className={`py-3 ${frozenCellClass(index, highlightContratoId === contrato.id)}`}
                        >
                          <Text className="text-sm font-semibold text-zinc-900 truncate max-w-xs" title={contrato.numeroContrato?.value || '-'}>
                            {contrato.numeroContrato?.value || '-'}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3">
                          {fianzaStats.emitidas === 0 && fianzaStats.reqPendientes === 0 ? (
                            <Text className="text-sm text-zinc-400">Sin fianzas</Text>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <div className="flex flex-wrap items-center gap-1">
                                {fianzaStats.vigentes > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                    {fianzaStats.vigentes} vig.
                                  </span>
                                )}
                                {fianzaStats.porVencer > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                    {fianzaStats.porVencer} por vencer
                                  </span>
                                )}
                                {fianzaStats.vencidas > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                    {fianzaStats.vencidas} venc.
                                  </span>
                                )}
                                {fianzaStats.porIniciar > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                                    {fianzaStats.porIniciar} por iniciar
                                  </span>
                                )}
                                {fianzaStats.terminadas > 0 && (
                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-200 text-zinc-700">
                                    {fianzaStats.terminadas} term.
                                  </span>
                                )}
                              </div>
                              {fianzaStats.reqPendientes > 0 && (
                                <Text className="text-xs font-medium text-orange-600">
                                  {fianzaStats.reqPendientes} por emitir
                                </Text>
                              )}
                              {fianzaStats.endososPendientes > 0 && (
                                <span className={`flex items-center gap-1 text-xs font-medium ${
                                  fianzaStats.endososUrgencia === 'vencido' || fianzaStats.endososUrgencia === 'urgente'
                                    ? 'text-red-600'
                                    : 'text-amber-600'
                                }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    fianzaStats.endososUrgencia === 'vencido' || fianzaStats.endososUrgencia === 'urgente'
                                      ? 'bg-red-500'
                                      : 'bg-amber-500'
                                  }`}></span>
                                  {fianzaStats.endososPendientes === 1
                                    ? 'Endoso pendiente'
                                    : `${fianzaStats.endososPendientes} endosos pendientes`}
                                </span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-sm">{contrato.proveedor?.value || '-'}</TableCell>
                        <TableCell className="py-3 text-sm">{contrato.contratante?.value || '-'}</TableCell>
                        <TableCell className="py-3 text-sm">{proyectosById[contrato.proyectoId]?.nombre || '-'}</TableCell>
                        <TableCell className="py-3 text-sm font-semibold text-azul-surelia">
                          {formatMonto(contrato.montoContrato?.value || 0)}
                        </TableCell>
                        <TableCell className="py-3">
                          {fianzaStats.montoAfianzado > 0 || fianzaStats.primaVigente > 0 ? (
                            <div className="text-xs">
                              <div className="text-zinc-700 font-semibold tabular-nums">
                                {formatMonto(fianzaStats.montoAfianzado)}
                              </div>
                              <div className="text-zinc-500 tabular-nums">
                                Prima: {formatMonto(fianzaStats.primaVigente)}
                              </div>
                            </div>
                          ) : (
                            <Text className="text-sm text-zinc-400">—</Text>
                          )}
                        </TableCell>
                        <TableCell className="py-3">
                          {contrato.status === 'procesando' ? (
                            <span className="px-3 py-1 inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-yellow-700" />
                              Procesando
                            </span>
                          ) : (
                            <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                              contrato.estatus?.value === 'activo' ? 'bg-green-100 text-green-800' :
                              contrato.estatus?.value === 'completado' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {contrato.estatus?.value || contrato.status || 'N/A'}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                      {expanded && (
                        <TableRow className="bg-azul-surelia/5">
                          {/* Spacer over the frozen columns. The panel is sticky, and at
                              the end of the scroll its containing block shoves it
                              back over the frozen strip — this opaque z-10 cell is
                              what it slides UNDER, instead of crossing the freeze
                              divider. Tint = bg-azul-surelia/5 flattened over white. */}
                          <TableCell colSpan={2} style={{ left: 0 }} className="sticky z-10 bg-[#F4F6F7] p-0" />
                          <TableCell colSpan={7} className="py-3 px-6">
                            <ContratoFianzasPanel
                              stats={fianzaStats}
                              contratoId={contrato.id}
                              onFianzaClick={openContrato}
                              onSubir={handleProveedorAction}
                              canUpload={!isReadOnly}
                              stickyOffset={contratosFrozen.total}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            {/* Freeze divider: lives OUTSIDE the scrolling element, so it is
                pinned to the boundary instead of riding along with the cells. */}
            {contratosFrozen.scrolled && (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
                style={{ left: contratosFrozen.dividerLeft, bottom: contratosFrozen.dividerBottom }}
              />
            )}
            </div>
            {filteredContracts.length === 0 && (
              <div className="text-center py-6 text-sm text-gray-500">
                No hay contratos que coincidan con los filtros.
              </div>
            )}
          </div>
          </>
        )}
      </div>
    );
  };

  const renderFianzasEmitidas = () => {

    const now = Date.now();
    const proveedorSet = new Set();
    allFianzas.forEach((f) => { if (f.contractProveedor) proveedorSet.add(f.contractProveedor); });
    const fianzasProveedorOptions = [
      { value: 'all', label: 'Todos los proveedores' },
      ...Array.from(proveedorSet).sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v })),
    ];

    // Requisitos still without a fianza, shown in their own table below the
    // emitidas: the estatus/vigencia filters are about an issued fianza, so
    // only search and proveedor apply here.
    const pendientesAll = computePendingFianzas(contracts, beneficiariosById, proyectosById);

    const q = normalizar(fianzasSearch.trim());
    const filteredFianzas = allFianzas.filter((f) => {
      const status = getFianzaStatus(f, now);
      if (fianzasEstatus !== 'all' && status.code !== fianzasEstatus) return false;
      if (fianzasProveedor !== 'all' && (f.contractProveedor || '') !== fianzasProveedor) return false;
      if (fianzasVigencia !== 'all') {
        const fin = f.finVigenciaFianza?.toMillis?.() || f.finVigenciaFianza || 0;
        if (fianzasVigencia === 'vencida') {
          if (!(fin && fin < now)) return false;
        } else {
          const dias = fianzasVigencia === 'v30' ? 30 : 90;
          if (!(fin > now && fin <= now + dias * 86400000)) return false;
        }
      }
      if (q) {
        const campos = [
          f.numeroFianza, f.tipoFianza || f.tipo, f.contractNumero, f.contractTitle,
          f.contractProveedor, f.proyectoNombre, f.beneficiarioNombre,
        ];
        if (!campos.some((c) => normalizar(c).includes(q))) return false;
      }
      return true;
    });

    const filteredPendientes = pendientesAll.filter((fr) => {
      if (fianzasProveedor !== 'all' && (fr.fiadoNombre || '') !== fianzasProveedor) return false;
      if (q) {
        const campos = [fr.tipo, fr.contractNumero, fr.contractTitle, fr.fiadoNombre, fr.proyectoNombre, fr.beneficiarioNombre];
        if (!campos.some((c) => normalizar(c).includes(q))) return false;
      }
      return true;
    });

    return (
      <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-wrap">
        <input
          type="text"
          value={fianzasSearch}
          onChange={(e) => setFianzasSearch(e.target.value)}
          placeholder="Buscar por número, contrato, proveedor..."
          className="w-full sm:max-w-xs px-3 py-2 border border-azul-surelia/30 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-azul-surelia/40 focus:border-azul-surelia"
        />
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-azul-surelia whitespace-nowrap">Proveedor:</label>
          <SearchableListbox
            value={fianzasProveedor}
            onChange={setFianzasProveedor}
            options={fianzasProveedorOptions}
            className="min-w-[220px] max-w-[260px]"
            placeholder="Selecciona proveedor"
            searchPlaceholder="Buscar proveedor..."
            popupClassName="w-[260px]"
          />
        </div>
        <select
          value={fianzasEstatus}
          onChange={(e) => setFianzasEstatus(e.target.value)}
          className="text-sm border border-azul-surelia/30 rounded-md px-2 py-2 text-azul-surelia"
        >
          {FIANZA_ESTATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select
          value={fianzasVigencia}
          onChange={(e) => setFianzasVigencia(e.target.value)}
          className="text-sm border border-azul-surelia/30 rounded-md px-2 py-2 text-azul-surelia"
        >
          {FIANZA_VIGENCIA_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {filteredFianzas.length !== allFianzas.length && (
          <Text className="text-xs text-zinc-500">
            {filteredFianzas.length} de {allFianzas.length} fianzas
          </Text>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Heading level={4} className="text-azul-surelia">Emitidas</Heading>
      </div>
      {filteredFianzas.length === 0 ? (
        <div className="bg-white rounded-lg border border-azul-surelia/15 py-6 text-center">
          <Text className="text-sm text-zinc-500">
            {allFianzas.length === 0
              ? 'Aún no se ha emitido ninguna fianza en este convenio.'
              : 'No hay fianzas que coincidan con los filtros.'}
          </Text>
        </div>
      ) : (
      <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
        <div className="relative">
          <Table onScroll={fianzasFrozen.onScroll}>
            <TableHead className="bg-azul-surelia">
              <TableRow>
                <TableHeader
                  ref={fianzasFrozen.setRef(0)}
                  style={{ left: fianzasFrozen.offsets[0] }}
                  className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                >Tipo</TableHeader>
                <TableHeader
                  ref={fianzasFrozen.setRef(1)}
                  style={{ left: fianzasFrozen.offsets[1] }}
                  className={`text-white font-semibold py-3 border-b-0 ${frozenHeadClass}`}
                >No. Fianza</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Contrato</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Proyecto</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Monto</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Vigencia</TableHeader>
                <TableHeader className="text-white font-semibold py-3 border-b-0">Estatus</TableHeader>
                {canVerComision && (
                  <>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Participación</TableHeader>
                    <TableHeader className="text-white font-semibold py-3 border-b-0">Comisión</TableHeader>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredFianzas.map((f, index) => {
                const status = getFianzaStatus(f, now);
                return (
                  <TableRow
                    key={f.id}
                    className={`group cursor-pointer hover:bg-azul-surelia/5 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                    onClick={() => openContrato(f.contractId)}
                  >
                    <TableCell
                      style={{ left: fianzasFrozen.offsets[0] }}
                      className={`py-3 text-sm ${frozenCellClass(index)}`}
                    >
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <span>{f.tipoFianza || f.tipo || '-'}</span>
                        {f.proveedorMismatch && (
                          <span
                            title={`Fiado XML: ${f.proveedorMismatch.fiadoXmlNombre}. Prevalece el proveedor del contrato (${f.proveedorMismatch.contractProveedor}).`}
                            className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold"
                          >
                            Fiado no coincide
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell
                      style={{ left: fianzasFrozen.offsets[1] }}
                      className={`py-3 text-sm font-mono ${frozenCellClass(index)}`}
                    >{formatNumeroFianza(f, '-')}</TableCell>
                    <TableCell className="py-3 text-sm">{f.contractNumero || '-'}</TableCell>
                    <TableCell className="py-3 text-sm">{f.proyectoNombre || '-'}</TableCell>
                    <TableCell className="py-3 text-sm font-semibold text-azul-surelia">
                      {formatMonto(f.montoAfianzado || 0)}
                    </TableCell>
                    <TableCell className="py-3 text-xs text-gray-600">
                      {formatDate(f.inicioVigenciaFianza)} — {formatDate(f.finVigenciaFianza)}
                    </TableCell>
                    <TableCell className="py-3">
                      {(() => {
                        const facturasV = getFacturasVigentes(ensureFacturas(f));
                        const pagadas = facturasV.filter((fc) => fc.pagada).length;
                        const pagoEstado =
                          facturasV.length > 0 && pagadas === facturasV.length
                            ? 'pagada'
                            : pagadas > 0
                            ? 'parcial'
                            : 'pendiente';
                        return (
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <Badge color={status.color}>{status.label}</Badge>
                            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${PAGO_BADGE[pagoEstado]}`}>
                              {pagoEstado === 'pagada'
                                ? 'Pagada'
                                : pagoEstado === 'parcial'
                                ? `Parcial ${pagadas}/${facturasV.length}`
                                : 'Pago pendiente'}
                            </span>
                          </div>
                        );
                      })()}
                    </TableCell>
                    {canVerComision && (
                      <>
                        <TableCell className="py-3">
                          {(() => {
                            const emisorNombre = f.xmlFactura?.emisor?.nombre || f.xmlFianza?.emisor?.nombre || '';
                            const override =
                              f.participacionOverride?.beneficiario != null && f.participacionOverride?.fiado != null
                                ? f.participacionOverride
                                : null;
                            const part = override || resolveParticipacion(emisorNombre, comisionConfig, convenio);
                            const esDefault =
                              !override &&
                              !convenio?.participacion?.[emisorNombre] &&
                              !comisionConfig?.participacionConvenio?.[emisorNombre];
                            // A locked comisionPct proves nothing: legacy uploads
                            // stamped the org's OWN % on convenio fianzas, and a
                            // retarifa locks a table's number. Only the explicit
                            // stamp means a person confirmed the fiado agent's %.
                            const confirmado = f.comisionPctConfirmada != null;
                            const partLabel = `${part.beneficiario}/${part.fiado}`;
                            const partTitle = override
                              ? `Participación negociada para esta fianza: convenio ${part.beneficiario}% / agente del fiado ${part.fiado}%.`
                              : `Distribución de la comisión: convenio ${part.beneficiario}% / agente del fiado ${part.fiado}%${esDefault ? ' (por defecto)' : ''}.`;
                            return (
                              <div className="flex items-center gap-1.5 whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setComisionEdit({
                                      fianza: f,
                                      contratoId: f.contractId,
                                      participacion: part,
                                    });
                                  }}
                                  title={`${partTitle} Clic para ajustarla.`}
                                  className={`inline-flex items-center gap-1 text-xs tabular-nums px-1.5 py-0.5 rounded border ${
                                    esDefault
                                      ? 'border-dashed border-zinc-300 text-zinc-500 hover:border-azul-surelia hover:text-azul-surelia'
                                      : 'border-azul-surelia/30 text-azul-surelia bg-azul-surelia/5 hover:border-azul-surelia'
                                  }`}
                                >
                                  {partLabel}
                                  {esDefault && <span className="text-[10px]">por defecto</span>}
                                  <PencilSquareIcon className="w-3 h-3" />
                                </button>
                                <span
                                  className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                                    confirmado
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-amber-100 text-amber-700 border border-amber-200'
                                  }`}
                                  title={confirmado
                                    ? 'El % de comisión del agente del fiado ya fue confirmado.'
                                    : 'El % de comisión es estimado o falta definirlo; confírmalo en la fianza.'}
                                >
                                  {confirmado ? '% confirmado' : '% por confirmar'}
                                </span>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="py-3">
                          {(() => {
                            const resumen = comisionResumenFor(f.id);
                            if (!resumen) return <span className="text-xs text-zinc-400">—</span>;
                            const chip = COBRO_CHIP[resumen.estado];
                            return (
                              <div className="flex items-center gap-1.5 whitespace-nowrap">
                                <span
                                  className="text-sm font-semibold text-zinc-800 tabular-nums"
                                  title={resumen.estado === 'parcial'
                                    ? `Comisión total de esta fianza. Cobrado ${formatMonto(resumen.cobrado)}, por cobrar ${formatMonto(resumen.pendiente)}.`
                                    : 'Comisión total de esta fianza en el ledger.'}
                                >
                                  {formatMonto(resumen.total)}
                                </span>
                                {chip && (
                                  <span
                                    className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${chip.cls}`}
                                    title={resumen.estado === 'parcial'
                                      ? `Cobrado ${formatMonto(resumen.cobrado)} de ${formatMonto(resumen.total)}.`
                                      : resumen.estado === 'pendiente'
                                      ? `Comisión sin marcar como cobrada: ${formatMonto(resumen.pendiente)}.`
                                      : resumen.estado === 'cobrada'
                                      ? 'Toda la comisión de esta fianza está marcada como cobrada.'
                                      : 'Los movimientos de esta fianza no dejan saldo por cobrar.'}
                                  >
                                    {chip.label}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        {/* Freeze divider: lives OUTSIDE the scrolling element, so it is pinned
            to the boundary instead of riding along with the cells. */}
        {fianzasFrozen.scrolled && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-0 w-0.5 bg-azul-surelia z-30"
            style={{ left: fianzasFrozen.dividerLeft, bottom: fianzasFrozen.dividerBottom }}
          />
        )}
        </div>
      </div>
      )}

      {/* ── Por emitir y futuras ───────────────────────────────────────── */}
      <div className="pt-2">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <Heading level={4} className="text-azul-surelia">Por emitir y futuras</Heading>
          <Text className="text-xs text-zinc-500">
            {filteredPendientes.length} requisito{filteredPendientes.length === 1 ? '' : 's'} sin fianza emitida
          </Text>
        </div>
        {filteredPendientes.length === 0 ? (
          <div className="bg-white rounded-lg border border-azul-surelia/15 py-6 text-center">
            <Text className="text-sm text-zinc-500">
              {pendientesAll.length === 0
                ? 'Todas las fianzas requeridas de este convenio ya fueron emitidas.'
                : 'Ningún requisito coincide con los filtros.'}
            </Text>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
            <Table>
              <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                <TableRow>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Tipo</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Estado</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Contrato</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Proveedor</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Proyecto</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">% del contrato</TableHeader>
                  <TableHeader
                    className="text-white font-semibold py-3 border-b-0 text-right"
                    title="Monto que deberá quedar afianzado cuando se emita la fianza."
                  >Por afianzar</TableHeader>
                  <TableHeader className="text-white font-semibold py-3 border-b-0">Emisión</TableHeader>
                  {!isReadOnly && <TableHeader className="text-white font-semibold py-3 border-b-0" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPendientes.map((fr, index) => {
                  const tone = fr.isFutura
                    ? { badge: 'bg-zinc-100 text-zinc-600', label: 'Futura', due: 'text-zinc-500' }
                    : fr.isOverdue
                    ? { badge: 'bg-red-100 text-red-800', label: 'Atrasada', due: 'text-red-700 font-medium' }
                    : { badge: 'bg-amber-100 text-amber-800', label: 'Por emitir', due: 'text-amber-700' };
                  return (
                    <TableRow
                      key={fr._key}
                      className={`cursor-pointer hover:bg-azul-surelia/5 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'}`}
                      onClick={() => openContrato(fr.contractId)}
                    >
                      <TableCell className="py-3 text-sm font-medium text-zinc-800">{fr.tipo || 'Fianza'}</TableCell>
                      <TableCell className="py-3">
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${tone.badge}`}>{tone.label}</span>
                      </TableCell>
                      <TableCell className="py-3 text-sm">{fr.contractNumero || '-'}</TableCell>
                      <TableCell className="py-3 text-sm">{fr.fiadoNombre || '-'}</TableCell>
                      <TableCell className="py-3 text-sm">{fr.proyectoNombre || '-'}</TableCell>
                      <TableCell className="py-3 text-sm tabular-nums">
                        {Number(fr.porcentaje) > 0 ? `${Number(fr.porcentaje)}%` : '—'}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-right tabular-nums font-semibold text-azul-surelia">
                        {formatMonto(fr.monto || 0)}
                      </TableCell>
                      <TableCell className={`py-3 text-xs ${tone.due}`}>{dueLabel(fr)}</TableCell>
                      {!isReadOnly && (
                        <TableCell className="py-3 text-right">
                          <Button
                            size="xs"
                            color="surelia"
                            outline
                            onClick={(e) => {
                              e.stopPropagation();
                              handleProveedorAction({
                                type: 'subir_fianza',
                                contratoId: fr.contractId,
                                frKey: fr._key,
                                tipo: fr.tipo,
                                monto: fr.monto,
                              });
                            }}
                          >
                            Subir
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      </div>
    );
  };

  const renderProyectos = () => {
    const contractsByProject = {};
    const unassigned = [];
    contracts.forEach((c) => {
      if (c.proyectoId) {
        if (!contractsByProject[c.proyectoId]) contractsByProject[c.proyectoId] = [];
        contractsByProject[c.proyectoId].push(c);
      } else {
        unassigned.push(c);
      }
    });

    // Aggregate the per-contract rollups so each proyecto card can judge
    // itself: exposure, prima, and what needs attention.
    const aggFor = (pContracts) => {
      const agg = {
        contratos: pContracts.length, montoAfianzado: 0, primaVigente: 0,
        vigentes: 0, porVencer: 0, vencidas: 0, reqPendientes: 0, endososPendientes: 0,
        rollups: new Map(),
      };
      pContracts.forEach((c) => {
        const r = getContratoFianzaStats(c);
        agg.rollups.set(c.id, r);
        agg.montoAfianzado += r.montoAfianzado;
        agg.primaVigente += r.primaVigente;
        agg.vigentes += r.vigentes;
        agg.porVencer += r.porVencer;
        agg.vencidas += r.vencidas;
        agg.reqPendientes += r.reqPendientes;
        agg.endososPendientes += r.endososPendientes;
      });
      return agg;
    };

    const proyectoCard = ({ key, nombre, pContracts, editable, proyecto }) => {
      const agg = aggFor(pContracts);
      return (
        <div key={key} className="bg-white rounded-lg shadow-sm border border-azul-surelia/20 overflow-hidden">
          <div className="px-4 py-3 bg-azul-surelia/5 border-b border-azul-surelia/10 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <FolderIcon className="w-5 h-5 text-azul-surelia/70 shrink-0" />
              <span className="font-semibold text-azul-surelia truncate">{nombre}</span>
              <Badge color="zinc">{agg.contratos} contrato{agg.contratos !== 1 ? 's' : ''}</Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="flex flex-wrap items-center gap-1">
                {agg.vigentes > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    {agg.vigentes} vig.
                  </span>
                )}
                {agg.porVencer > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                    {agg.porVencer} por vencer
                  </span>
                )}
                {agg.vencidas > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                    {agg.vencidas} venc.
                  </span>
                )}
                {agg.reqPendientes > 0 && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">
                    {agg.reqPendientes} por emitir
                  </span>
                )}
              </div>
              {agg.montoAfianzado > 0 && (
                <div className="text-right">
                  <div className="text-sm font-bold text-azul-surelia tabular-nums">
                    {formatMonto(agg.montoAfianzado)}
                  </div>
                  <div className="text-[10px] uppercase tracking-wide text-zinc-400">afianzado</div>
                </div>
              )}
              {editable && (
                <Button plain onClick={() => { setEditProyecto(proyecto); setIsProyectoOpen(true); }}>
                  <PencilSquareIcon className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          {pContracts.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                  <TableRow>
                    <TableHeader className="text-white font-semibold w-8" />
                    <TableHeader className="text-white font-semibold">Contrato</TableHeader>
                    <TableHeader className="text-white font-semibold">Proveedor</TableHeader>
                    <TableHeader className="text-white font-semibold">Monto Contrato</TableHeader>
                    <TableHeader className="text-white font-semibold">Afianzado</TableHeader>
                    <TableHeader className="text-white font-semibold">Fianzas</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pContracts.map((c, i) => {
                    const r = agg.rollups.get(c.id);
                    const cExpanded = expandedContratoIds.has(c.id);
                    return (
                      <React.Fragment key={c.id}>
                      <TableRow
                        data-contrato-row={c.id}
                        className={`cursor-pointer transition-colors duration-700 hover:bg-azul-surelia/5 ${
                          highlightContratoId === c.id
                            ? 'bg-cian-digital/25'
                            : i % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                        }`}
                        onClick={() => openContrato(c.id)}
                      >
                        <TableCell className="py-3 pr-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleContratoExpanded(c.id);
                            }}
                            className="p-1 rounded text-zinc-400 hover:text-azul-surelia hover:bg-azul-surelia/10 transition-all duration-200"
                            title={cExpanded ? 'Ocultar fianzas' : 'Ver fianzas'}
                          >
                            {cExpanded ? (
                              <ChevronDownIcon className="w-4 h-4" />
                            ) : (
                              <ChevronRightIcon className="w-4 h-4" />
                            )}
                          </button>
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="text-sm font-medium text-azul-surelia">{c.numeroContrato?.value || '-'}</div>
                          {c.tituloContrato?.value && (
                            <div className="text-xs text-zinc-500 truncate max-w-[260px]" title={c.tituloContrato.value}>
                              {c.tituloContrato.value}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-sm">{c.proveedor?.value || '-'}</TableCell>
                        <TableCell className="py-3 text-sm tabular-nums">{formatMonto(c.montoContrato?.value || 0)}</TableCell>
                        <TableCell className="py-3 text-sm tabular-nums text-azul-surelia font-semibold">
                          {r.montoAfianzado > 0 ? formatMonto(r.montoAfianzado) : '—'}
                        </TableCell>
                        <TableCell className="py-3">
                          {r.emitidas === 0 && r.reqPendientes === 0 ? (
                            <span className="text-sm text-zinc-400">Sin fianzas</span>
                          ) : (
                            <div className="flex flex-wrap items-center gap-1">
                              {r.vigentes > 0 && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">{r.vigentes} vig.</span>
                              )}
                              {r.vencidas > 0 && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">{r.vencidas} venc.</span>
                              )}
                              {r.terminadas > 0 && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-zinc-200 text-zinc-700">{r.terminadas} term.</span>
                              )}
                              {r.reqPendientes > 0 && (
                                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-orange-100 text-orange-700">{r.reqPendientes} por emitir</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      {cExpanded && (
                        <TableRow className="bg-azul-surelia/5">
                          <TableCell colSpan={6} className="py-3 px-6">
                            <ContratoFianzasPanel
                              stats={r}
                              contratoId={c.id}
                              onFianzaClick={openContrato}
                              onSubir={handleProveedorAction}
                              canUpload={!isReadOnly}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="px-4 py-6 text-center">
              <Text className="text-sm text-zinc-400">Sin contratos en este proyecto</Text>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-4">
        {!isReadOnly && (
          <div className="flex justify-end">
            <Button outline onClick={() => setIsProyectoOpen(true)}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Nuevo Proyecto
            </Button>
          </div>
        )}
        {proyectos.length === 0 && unassigned.length === 0 ? (
          <div className="text-center py-8">
            <Text>No hay proyectos. Los contratos se pueden agrupar por proyecto.</Text>
          </div>
        ) : (
          <>
            {proyectos.map((p) =>
              proyectoCard({
                key: p.id,
                nombre: p.nombre,
                pContracts: contractsByProject[p.id] || [],
                editable: !isReadOnly,
                proyecto: p,
              })
            )}
            {unassigned.length > 0 &&
              proyectoCard({
                key: '__sin_proyecto__',
                nombre: 'Sin proyecto',
                pContracts: unassigned,
                editable: false,
                proyecto: null,
              })}
          </>
        )}
      </div>
    );
  };

  const renderNotas = () => (
    <SectionCard
          tone="gradient"
      icon={DocumentTextIcon}
      title="Notas del Equipo"
      bodyClassName="p-4"
      action={
        <select
          value={filterCategoria}
          onChange={(e) => setFilterCategoria(e.target.value)}
          className="text-xs border border-azul-surelia/30 rounded-lg px-2 py-1 text-gray-600 focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none"
        >
          <option value="all">Todas</option>
          {NOTA_CATEGORIAS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      }
    >
      <div className="mb-4 space-y-2">
        <Textarea
          value={newNota}
          onChange={(e) => setNewNota(e.target.value)}
          placeholder="Escribe una nota para el equipo..."
          rows={2}
          className="flex-1"
        />
        <div className="flex items-center justify-between">
          <select
            value={newNotaCategoria}
            onChange={(e) => setNewNotaCategoria(e.target.value)}
            className="text-xs border border-azul-surelia/30 rounded-lg px-2 py-1 text-gray-600 focus:border-azul-surelia focus:ring-2 focus:ring-azul-surelia/20 outline-none"
          >
            {NOTA_CATEGORIAS.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
          <Button onClick={handleAddNota} disabled={!newNota.trim() || savingNota}>
            {savingNota ? '...' : 'Agregar'}
          </Button>
        </div>
      </div>
      {notasLoading ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-azul-surelia" />
        </div>
      ) : filteredNotas.length === 0 ? (
        <Text className="text-sm text-gray-500 text-center py-4">No hay notas</Text>
      ) : (
        <div className="relative max-h-96 overflow-y-auto">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
          <div className="relative space-y-1">
            {filteredNotas.map((nota) => {
              const ts = nota.createdAt?.toMillis?.() || nota.createdAt;
              const date = ts ? new Date(ts).toLocaleString('es-MX', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
              }) : '';
              const catStyle = getCategoriaStyle(nota.categoria);
              const isCompleted = nota.completed === true;
              return (
                <div key={nota.id} className="flex gap-3 py-3">
                  <div className="flex-shrink-0">
                    <Avatar
                      initials={getInitials(nota.userName)}
                      className={`size-8 ${getAvatarColor(nota.createdBy)}`}
                      alt={nota.userName || 'Usuario'}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900 text-sm">{nota.userName || 'Usuario'}</span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${catStyle.color}`}>
                        {catStyle.label}
                      </span>
                    </div>
                    <div className="flex items-start gap-2 mt-1">
                      <input
                        type="checkbox"
                        checked={isCompleted}
                        onChange={() => handleToggleNota(nota.id, isCompleted)}
                        className="mt-1 rounded border-gray-300 text-azul-surelia focus:ring-azul-surelia cursor-pointer"
                      />
                      <p className={`text-sm whitespace-pre-wrap flex-1 ${
                        isCompleted ? 'line-through text-gray-400' : 'text-gray-700'
                      }`}>
                        {nota.texto}
                      </p>
                    </div>
                    <div className="text-xs text-gray-400 mt-1">{date}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </SectionCard>
  );

  const renderBeneficiariosSection = () => (
    <SectionCard
          tone="gradient"
      icon={BuildingOffice2Icon}
      title="Beneficiarios"
      action={!isReadOnly && (
        <Button color="white" onClick={() => setIsBeneficiarioOpen(true)}>
          <PlusIcon className="h-4 w-4 mr-1" />
          Agregar
        </Button>
      )}
    >
      {beneficiarios.length === 0 ? (
        <Text className="text-sm text-gray-400 italic">Ningún beneficiario registrado.</Text>
      ) : (
        <div className="border border-azul-surelia/15 rounded-lg divide-y divide-gray-100">
          {beneficiarios.map((b) => {
            const bContracts = contracts.filter((c) => c.beneficiarioId === b.id);
            return (
              <div key={b.id} className="flex items-center justify-between px-3 py-2">
                <div className="min-w-0 flex-1">
                  <Text className="text-sm font-medium truncate">{b.nombre}</Text>
                  {b.rfc && <Text className="text-xs text-gray-400 font-mono">{b.rfc}</Text>}
                </div>
                <div className="flex items-center gap-2">
                  <Badge>{bContracts.length} contrato{bContracts.length !== 1 ? 's' : ''}</Badge>
                  {!isReadOnly && (
                    <Button plain onClick={() => { setEditBeneficiario(b); setIsBeneficiarioOpen(true); }}>
                      <PencilSquareIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );

  const renderConfiguracion = () => (
    <div className="space-y-6">
      {/* Info card */}
      <div className="bg-white rounded-lg shadow-sm border border-azul-surelia/15 overflow-hidden">
        <div className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95 px-5 py-3 flex items-center justify-between">
          <span className="text-white font-semibold text-sm flex items-center gap-2">
            <InformationCircleIcon className="w-4 h-4 text-white/80" />
            Información del Convenio
          </span>
          {!isReadOnly && !isEditingConfig && (
            <button onClick={() => setIsEditingConfig(true)} className="text-white/80 hover:text-white transition-colors">
              <PencilSquareIcon className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="p-5">
          {isEditingConfig ? (
            <div className="space-y-4">
              <Field>
                <Label>Nombre</Label>
                <Input value={editNombre} onChange={(e) => setEditNombre(e.target.value)} />
              </Field>
              <Field>
                <Label>Descripción</Label>
                <Textarea value={editDescripcion} onChange={(e) => setEditDescripcion(e.target.value)} rows={3} />
              </Field>
              <div className="flex gap-2">
                <Button onClick={handleSaveConfig} disabled={dialogLoading}>
                  {dialogLoading ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button plain onClick={() => setIsEditingConfig(false)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Text className="text-xs text-gray-500 uppercase tracking-wide">Nombre</Text>
                <Text className="font-medium mt-0.5">{convenio.nombre}</Text>
              </div>
              <div>
                <Text className="text-xs text-gray-500 uppercase tracking-wide">Estatus</Text>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge color={convenio.estatus === 'activo' ? 'green' : 'zinc'}>
                    {convenio.estatus === 'activo' ? 'Activo' : 'Inactivo'}
                  </Badge>
                  {!isReadOnly && (
                    <button onClick={handleToggleStatus} className="text-xs text-azul-surelia hover:underline">
                      {convenio.estatus === 'activo' ? 'Desactivar' : 'Activar'}
                    </button>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Text className="text-xs text-gray-500 uppercase tracking-wide">Descripción</Text>
                <Text className="mt-0.5">{convenio.descripcion || '-'}</Text>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Beneficiarios (moved from old standalone tab) */}
      {renderBeneficiariosSection()}

      {/* Acceso */}
      {!isReadOnly && (
        <SectionCard
          tone="gradient"
          icon={UserGroupIcon}
          title="Acceso de Beneficiarios"
          action={
            <Button color="white" onClick={() => setIsInviteOpen(true)}>
              <PlusIcon className="h-4 w-4 mr-1" />
              Invitar Usuario
            </Button>
          }
        >
          {usersWithAccess.length > 0 && (
            <div className="border border-azul-surelia/15 rounded-lg divide-y divide-gray-100">
              {usersWithAccess.map((u) => {
                const access = u.convenioAccess?.[convenioId];
                return (
                  <div key={u.id} className="flex items-center justify-between px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <Text className="text-sm font-medium truncate">{u.nombre || u.correo}</Text>
                      <Text className="text-xs text-gray-400">{u.correo}</Text>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      access?.fullAccess ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {access?.fullAccess
                        ? 'Completo'
                        : `${(access?.proyectoIds || []).length} proyecto${(access?.proyectoIds || []).length !== 1 ? 's' : ''}`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {usersWithAccess.length === 0 && (
            <Text className="text-xs text-gray-400 italic">Ningún usuario tiene acceso a este convenio aún.</Text>
          )}
        </SectionCard>
      )}

      {/* Participación de comisiones (owner-only: money config) */}
      {userData?.rol === 'owner' && (
        <ParticipacionConvenioCard
          convenio={convenio}
          convenioId={convenioId}
          contracts={contracts}
          updateConvenio={updateConvenio}
          user={user}
          userData={userData}
        />
      )}

      {/* Notas (moved from old Resumen) */}
      {!isReadOnly && renderNotas()}

      {/* Historial */}
      <SectionCard tone="gradient" icon={ClockIcon} title="Historial de Actividad">
        <BitacoraTimeline filters={{ convenioId, limit: 100 }} compact pageSize={20} />
      </SectionCard>
    </div>
  );

  const visibleTabs = TABS.filter((tab) => {
    if (tab.key === 'proyectos' && proyectos.length === 0 && isReadOnly) return false;
    if (tab.key === 'configuracion' && isReadOnly) return false;
    // The comisión ledger is the despacho's money — owner eyes only.
    if (tab.key === 'comisiones' && userData?.rol !== 'owner') return false;
    return true;
  });

  return (
    <div>
      <ConvenioHeader
        convenio={convenio}
        beneficiarios={beneficiarios}
        leftAction={(
          <Button plain onClick={() => navigate(location.state?.backTo || '/app/convenios')}>
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
        )}
        actions={!isReadOnly && beneficiarios.length > 0 && (
          <Button color="surelia" onClick={() => setIsContractOpen(true)}>
            Nuevo Contrato
          </Button>
        )}
      />

      {/* Tabs */}
      <div className="overflow-x-auto mb-6 border-b border-azul-surelia/20">
        <div className="flex gap-1 min-w-max">
          {visibleTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 sm:px-6 py-3 font-semibold focus:outline-none transition-all duration-200 border-b-2 whitespace-nowrap text-sm sm:text-base ${
                activeTab === tab.key
                  ? 'text-azul-surelia border-azul-surelia bg-azul-surelia/5'
                  : 'text-gray-500 border-transparent hover:text-azul-surelia hover:border-azul-surelia/40 hover:bg-azul-surelia/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'proveedores' && (
        <ProveedoresChaseList
          proveedoresAgg={proveedoresAgg}
          mode={isReadOnly ? 'client' : 'broker'}
          onContractClick={openContrato}
          onAction={handleProveedorAction}
          expandKey={expandKey}
        />
      )}
      {activeTab === 'resumen' && (
        <div className="space-y-4">
          <ConvenioResumen
            contracts={contracts}
            beneficiarios={beneficiarios}
            beneficiariosById={beneficiariosById}
            proyectosById={proyectosById}
            afianzadorasById={afianzadorasById}
            onProveedorClick={handleProveedorClickFromResumen}
            onFianzaClick={(f) => openContrato(f.contractId)}
          />
          {/* Estadísticas embedded (charts only — the Salud card above already
              carries the headline numbers). Broker-only. */}
          {!isReadOnly && (
            <SectionCard icon={PresentationChartLineIcon} title="Estadísticas">
              <ClientEstadisticas convenioId={convenioId} showResumen={false} />
            </SectionCard>
          )}
        </div>
      )}
      {activeTab === 'contratos' && renderContratos()}
      {activeTab === 'fianzas' && renderFianzasEmitidas()}
      {activeTab === 'comisiones' && userData?.rol === 'owner' && (
        <ClientComisiones convenioId={convenioId} />
      )}
      {activeTab === 'proyectos' && renderProyectos()}
      {activeTab === 'configuracion' && renderConfiguracion()}

      {/* Dialogs */}
      {comisionEdit && (
        <ComisionEditorDialog
          isOpen
          onClose={() => setComisionEdit(null)}
          fianza={comisionEdit.fianza}
          contratoId={comisionEdit.contratoId}
          movimiento={comisionMovFor(comisionEdit.fianza?.id)}
          participacion={comisionEdit.participacion}
          showSplit
        />
      )}

      <BeneficiarioDialog
        isOpen={isBeneficiarioOpen}
        onClose={() => { setIsBeneficiarioOpen(false); setEditBeneficiario(null); }}
        onSubmit={editBeneficiario ? handleEditBeneficiario : handleAddBeneficiario}
        beneficiario={editBeneficiario}
        loading={dialogLoading}
      />

      <ProyectoDialog
        isOpen={isProyectoOpen}
        onClose={() => { setIsProyectoOpen(false); setEditProyecto(null); }}
        onSubmit={editProyecto ? handleEditProyecto : handleAddProyecto}
        proyecto={editProyecto}
        loading={dialogLoading}
      />

      <ConvenioContractDialog
        isOpen={isContractOpen}
        onClose={() => setIsContractOpen(false)}
        onSubmit={handleAddContract}
        beneficiarios={beneficiarios}
        proyectos={proyectos}
        loading={dialogLoading}
      />

      <InviteConvenioUserDialog
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        convenioId={convenioId}
        convenioName={convenio?.nombre || ''}
        proyectos={proyectos}
      />

      <UploadFianza
        isOpen={uploadFianzaOpen}
        onClose={() => { setUploadFianzaOpen(false); setUploadFianzaPayload(null); }}
        contratoId={uploadFianzaPayload?.contratoId}
        contratoProveedor={uploadFianzaPayload?.contratoProveedor || ''}
        pendingFianzas={uploadFianzaPayload?.pendingFianzas || []}
        convenioId={convenioId}
        convenioName={convenio?.nombre || ''}
      />

      {selectedContrato && (
        <ContratoView
          contrato={selectedContrato}
          isOpen={isContratoViewOpen}
          onClose={() => {
            const cameFrom = selectedContrato?.id || null;
            setIsContratoViewOpen(false);
            setSelectedContrato(null);
            if (cameFrom) setHighlightContratoId(cameFrom);
          }}
          convenio={convenio}
          beneficiariosById={beneficiariosById}
        />
      )}
    </div>
  );
};

export default ConvenioDetails;
